import { PatientSearchBackendResponse } from '../api/patient-api.interface';
import { PatientSearchResult } from '@model/patient/patient.model';

interface NestedDto {
  id: string;
  name: string;
}

const EMPTY_NESTED_DTO: NestedDto = { id: '', name: '' };

export class PatientSearchMapper {
  static fromBackendDto(
    dto: PatientSearchBackendResponse
  ): PatientSearchResult {
    return {
      found: dto.found,
      message: dto.message,
      idNumber: dto.idNumber,
      identifierTypeCode: dto.identifierTypeCode,
      identifierTypeName: dto.identifierTypeName,
      givenName: dto.givenName,
      middleName: dto.middleName,
      familyName: dto.familyName,
      maternalFamilyName: dto.maternalFamilyName,
      secondFamilyName: dto.secondFamilyName,
      emailAddress: dto.emailAddress ?? dto.email ?? '',
      birthDate: dto.birthDate ?? dto.dateOfBirth ?? '',
      dateOfBirth: dto.dateOfBirth,
      administrativeSex: dto.administrativeSex,
      biologicalSex: dto.biologicalSex,
      genderIdentity: dto.genderIdentity,
      city: dto.city ?? '',
      state: dto.state ?? '',
      country: dto.country ?? '',
      placerOrderNumber: dto.placerOrderNumber ?? '',
      examName: this.extractExamName(dto.examName),
      examNameId: this.extractExamNameId(dto.examName),
      examCode: dto.examCode ?? '',
      region: dto.region,
      admissionDate: dto.admissionDate ?? '',
      caseNumber: dto.caseNumber,
      site: this.mapNestedDtoOrDefault(dto.site),
      specimenType: this.mapNestedDtoOrDefault(dto.specimenType),
      examGroup: this.mapNestedDtoOrDefault(dto.examGroup),
      regional: dto.regional ? this.mapNestedDto(dto.regional) : undefined,
      priority: dto.priority ? this.mapNestedDto(dto.priority) : undefined,
      email: dto.email,
      mobilePhoneCountryCode: dto.mobilePhoneCountryCode,
      mobilePhone: dto.mobilePhone,
      phoneCountryCode: dto.phoneCountryCode,
      phone: dto.phone,
      requiresExamSelection: dto.requiresExamSelection,
      orderExternalReference: dto.orderExternalReference,
      orderNumber: dto.orderNumber,
      sampleCode: dto.sampleCode,
      comments: dto.comments,
      receptionDate: dto.receptionDate,
      orderStatus: dto.orderStatus
    };
  }

  private static extractExamName(
    examName: string | { id: string; name: string } | undefined
  ): string {
    if (typeof examName === 'string') {
      return examName;
    }
    return examName?.name ?? '';
  }

  private static extractExamNameId(
    examName: string | { id: string; name: string } | undefined
  ): string | undefined {
    if (typeof examName === 'object' && examName !== null) {
      return examName?.id;
    }
    return undefined;
  }

  private static mapNestedDtoOrDefault<
    T extends { id: string | number; name: string }
  >(dto: T | undefined | null): NestedDto {
    return dto ? this.mapNestedDto(dto) : EMPTY_NESTED_DTO;
  }

  private static mapNestedDto<T extends { id: string | number; name: string }>(
    dto: T
  ): NestedDto {
    return {
      id: typeof dto.id === 'number' ? dto.id.toString() : dto.id,
      name: dto.name
    };
  }
}
